import {
  centsFrom,
  formatFigure,
  NOTIFICATION_ANY,
  NOTIFICATION_NONE,
  type NotificationUnit,
  ROLLUP_SCHEDULE_LABEL,
  ROLLUP_SCHEDULES,
  type RollupSchedule,
} from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  type RouterInputs,
  type RouterOutputs,
  useTRPC,
  useTRPCClient,
} from '../lib/trpc'
import { hasUnsavedChanges } from '../lib/unsaved'
import { Choice } from './Choice'
import {
  Button,
  Field,
  FieldSection,
  type FootAction,
  MoneyInput,
  Notice,
  SettingBlock,
} from './index'
import { Select } from './Select'
import { useToast } from './Toast'
import './notification-choices.css'

type Org = RouterOutputs['notification']['mine'][number]
export type Topic = Org['topics'][number]
type Filter = Topic['filters'][number]
type Answers = Topic['choice']['filters']
type Delivery = 'email' | 'rollup'

/**
 * Whose choice the rows edit: the reader's own, or a staff member's, set by an
 * admin of their org. It is one choice either way — the same row, read and
 * written through the procedure that may reach it.
 */
export type ChoiceOwner =
  | { kind: 'own' }
  | { kind: 'staff'; personId: string; name: string }

/**
 * A topic opened to narrow, the org it is chosen at, and whose choice it is. A
 * product's own section — Studio's books — has no org, and no space.
 */
export type NarrowTarget = {
  spaceId: string | null
  orgName: string | null
  topic: Topic
  owner: ChoiceOwner
}

const OWN: ChoiceOwner = { kind: 'own' }

/** How a sentence names the person hearing: "you hear", "Zelig hears". */
const hears = (owner: ChoiceOwner) =>
  owner.kind === 'own' ? 'you hear' : `${owner.name} hears`

/**
 * What a person hears about, at every org they belong to.
 *
 * A section per org, headed by the org and its product, so a choice always
 * says which org and which product it is about — and a section for a product's
 * own topics, headed by the product alone. Each topic is told as it
 * happens, listed in a roll-up on a schedule of its own, or both: two answers
 * that take effect as they are given, like a switch. What a topic is narrowed
 * by opens in the account's own sheet, since it is a draft with a commit.
 *
 * Generated from what the engine offers, so a topic a context registers next
 * appears here without this file changing.
 */
export function NotificationChoices({
  onNarrow,
}: {
  onNarrow: (target: NarrowTarget) => void
}) {
  const trpc = useTRPC()
  const orgs = useQuery(trpc.notification.mine.queryOptions())
  if (orgs.isLoading) return <Notice>Loading…</Notice>
  if (orgs.error) return <Notice tone="error">{orgs.error.message}</Notice>
  if ((orgs.data ?? []).length === 0) return null
  return (
    <>
      {(orgs.data ?? []).map((o) => (
        <FieldSection
          key={o.spaceId ?? o.productName}
          title={o.orgName ? `${o.orgName} — ${o.productName}` : o.productName}
        >
          <TopicRows
            spaceId={o.spaceId}
            orgName={o.orgName}
            topics={o.topics}
            owner={OWN}
            onNarrow={onNarrow}
          />
        </FieldSection>
      ))}
    </>
  )
}

/**
 * One org's topics, as rows of choices for whoever `owner` names — the
 * account screen's section, and the Notifications tab of a staff member's
 * sheet, are the same rows.
 */
export function TopicRows({
  spaceId,
  orgName,
  topics,
  owner,
  onNarrow,
}: {
  spaceId: string | null
  orgName: string | null
  topics: readonly Topic[]
  owner: ChoiceOwner
  onNarrow: (target: NarrowTarget) => void
}) {
  return (
    <>
      {topics.map((t) => (
        <TopicRow
          key={t.key}
          spaceId={spaceId}
          topic={t}
          owner={owner}
          onNarrow={() => onNarrow({ spaceId, orgName, topic: t, owner })}
        />
      ))}
    </>
  )
}

/** A save's variables, as a pending one is read back to learn the latest answer. */
type SetChoiceVariables = Pick<Topic['choice'], 'email' | 'rollup'>

/** Where one topic's saves queue, so they land in the order they were made. */
const scopeOf = (spaceId: string | null, topic: string, owner: ChoiceOwner) =>
  `notification-choice|${spaceId ?? 'none'}|${topic}|${owner.kind === 'own' ? 'own' : owner.personId}`

type SetChoice = RouterInputs['notification']['setChoice']

/**
 * Saving one topic's choice. Saves of one topic run one after another (`scope`),
 * since each states the whole choice: two in flight could land out of order
 * and leave the first standing.
 */
function useSaveChoice(
  spaceId: string | null,
  topic: string,
  owner: ChoiceOwner,
) {
  const trpc = useTRPC()
  const client = useTRPCClient()
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: async (choice: SetChoice) => {
      if (owner.kind === 'own') {
        return client.notification.setChoice.mutate(choice)
      }
      // A staff member's choices are set at their org; a product's own are theirs.
      if (choice.spaceId === null) {
        throw new Error('This choice is set by the person themselves.')
      }
      return client.notification.setStaffChoice.mutate({
        ...choice,
        spaceId: choice.spaceId,
        personId: owner.personId,
      })
    },
    scope: { id: scopeOf(spaceId, topic, owner) },
    onSuccess: async () => {
      /* One choice, read two ways: an admin may be setting their own row from
         the staff sheet, and their account screen must not show it stale. */
      await Promise.all([
        qc.invalidateQueries({ queryKey: trpc.notification.mine.queryKey() }),
        owner.kind === 'staff' &&
          spaceId !== null &&
          qc.invalidateQueries({
            queryKey: trpc.notification.staffChoices.queryKey({
              spaceId,
              personId: owner.personId,
            }),
          }),
      ])
      toast('Saved.')
    },
  })
}

/** A topic's choice as the cache holds it now, for whoever owns it. */
function cachedChoice(
  qc: ReturnType<typeof useQueryClient>,
  trpc: ReturnType<typeof useTRPC>,
  t: NarrowTarget,
): Topic['choice'] | undefined {
  const topics =
    t.owner.kind === 'own'
      ? qc
          .getQueryData(trpc.notification.mine.queryKey())
          ?.find((o) => o.spaceId === t.spaceId)?.topics
      : t.spaceId === null
        ? undefined
        : qc.getQueryData(
            trpc.notification.staffChoices.queryKey({
              spaceId: t.spaceId,
              personId: t.owner.personId,
            }),
          )
  return topics?.find((x) => x.key === t.topic.key)?.choice
}

/** The schedule a newly ticked roll-up starts on: each morning. */
const FIRST_SCHEDULE: RollupSchedule = 'daily_am'

function TopicRow({
  spaceId,
  topic,
  owner,
  onNarrow,
}: {
  spaceId: string | null
  topic: Topic
  owner: ChoiceOwner
  onNarrow: () => void
}) {
  const save = useSaveChoice(spaceId, topic.key, owner)
  /* The answer shows at once and the controls stay live while it saves: a
     key both moves focus and picks, so disabling them mid-save would drop the
     keyboard to the top of the sheet. */
  const shown = save.isPending
    ? {
        email: save.variables.email,
        rollup: save.variables.rollup,
        filters: save.variables.filters,
      }
    : topic.choice
  const put = (next: Partial<typeof shown>) =>
    save.mutate({
      spaceId,
      topic: topic.key,
      ...shown,
      ...next,
    })
  const delivery: Delivery[] = [
    ...(shown.email ? (['email'] as const) : []),
    ...(shown.rollup ? (['rollup'] as const) : []),
  ]
  const narrowing = summary(topic, shown.filters)
  return (
    <SettingBlock
      title={topic.label}
      description={topic.description}
      detail={
        <>
          {shown.rollup && (
            <Field label="Roll-up">
              <Select<RollupSchedule>
                value={shown.rollup}
                onValueChange={(rollup) => put({ rollup })}
                options={ROLLUP_SCHEDULES.map((s) => ({
                  value: s,
                  label: ROLLUP_SCHEDULE_LABEL[s],
                }))}
              />
            </Field>
          )}
          {topic.filters.length > 0 && (
            <div className="choices__narrowing">
              <span>{narrowing}</span>
              <Button
                variant="quiet"
                onClick={onNarrow}
                aria-label={`Change which ${topic.label.toLowerCase()} ${hears(owner)} about`}
                data-narrow={`${spaceId ?? 'none'}|${topic.key}`}
              >
                Change
              </Button>
            </div>
          )}
          {topic.choice.setBy && !save.isPending && (
            <span className="choices__set-by">Set by {topic.choice.setBy}</span>
          )}
          {save.error && <Notice tone="error">{save.error.message}</Notice>}
        </>
      }
    >
      <Choice<Delivery>
        multiple
        inline
        ariaLabel={`How ${hears(owner)} about ${topic.label.toLowerCase()}`}
        value={delivery}
        onValueChange={(next) =>
          put({
            email: next.includes('email'),
            rollup: next.includes('rollup')
              ? (shown.rollup ?? FIRST_SCHEDULE)
              : null,
          })
        }
        options={[
          { value: 'email', label: 'As it happens' },
          { value: 'rollup', label: 'In a roll-up' },
        ]}
      />
    </SettingBlock>
  )
}

/** A `oneOf` filter's options, its "none" first where it has one. */
function optionsOf(f: Extract<Filter, { type: 'oneOf' }>) {
  return [
    ...(f.noneLabel ? [{ value: NOTIFICATION_NONE, label: f.noneLabel }] : []),
    ...f.options,
  ]
}

/**
 * What a topic is narrowed by, in a line: each filter answered, in order, or
 * all of the topic where none is.
 */
function summary(topic: Topic, answers: Answers): string {
  const parts: string[] = []
  for (const f of topic.filters) {
    const answer = answers[f.key]
    if (f.type === 'oneOf') {
      if (!Array.isArray(answer)) continue
      if (answer.includes(NOTIFICATION_ANY)) {
        parts.push(`All ${f.label.toLowerCase()}`)
        continue
      }
      const labels = new Map(optionsOf(f).map((o) => [o.value, o.label]))
      const names = answer.flatMap((v) => {
        const label = labels.get(v)
        return label ? [label] : []
      })
      // Picks the org no longer offers match nothing, so the line says so.
      parts.push(
        names.length > 0
          ? names.join(', ')
          : `No ${f.label.toLowerCase()} still offered`,
      )
    } else if (typeof answer === 'number') {
      parts.push(`${formatFigure(f.unit, answer)} or more`)
    }
  }
  return parts.length > 0
    ? parts.join(' · ')
    : `All ${topic.label.toLowerCase()}`
}

/** A floor as typed, from its stored figure in the filter's unit. */
function floorTyped(unit: NotificationUnit, n: number): string {
  switch (unit) {
    case 'cents':
      return String(n / 100)
    default: {
      const unhandled: never = unit
      return unhandled
    }
  }
}

/** What was typed, as the stored figure: null for nothing, NaN for nonsense. */
function floorRead(unit: NotificationUnit, typed: string): number | null {
  if (typed.trim() === '') return null
  switch (unit) {
    case 'cents': {
      const cents = centsFrom(typed)
      return cents === null || cents < 0 ? Number.NaN : cents
    }
    default: {
      const unhandled: never = unit
      return unhandled
    }
  }
}

/** How a `oneOf` filter is answered: everything, or only some of its options. */
type Scope = 'all' | 'only'

/** A topic's narrowing while it is being changed. */
type Draft = {
  scopes: Record<string, Scope>
  picks: Record<string, string[]>
  floors: Record<string, string>
  /**
   * A stored answer none of whose picks the org still offers, kept as it was:
   * it could not be shown, and saving it as "every one" would widen it
   * without anybody choosing that.
   */
  kept: Record<string, string[]>
}

/**
 * A stored answer as the draft holds it. "Only" with nothing picked is every
 * option. A stored pick the org no longer offers is left out, since it could
 * not be shown, so it could not be unticked, and saving it back is refused.
 */
function draftOf(topic: Topic, answers: Answers): Draft {
  const draft: Draft = { scopes: {}, picks: {}, floors: {}, kept: {} }
  for (const f of topic.filters) {
    const answer = answers[f.key]
    if (f.type === 'oneOf') {
      const offered = new Set(optionsOf(f).map((o) => o.value))
      offered.add(NOTIFICATION_ANY)
      draft.scopes[f.key] = Array.isArray(answer) ? 'only' : 'all'
      const picks = Array.isArray(answer)
        ? answer.filter((v) => offered.has(v))
        : []
      draft.picks[f.key] = picks.filter((v) => v !== NOTIFICATION_ANY)
      if (Array.isArray(answer) && answer.length > 0 && picks.length === 0) {
        draft.kept[f.key] = answer
      }
    } else {
      draft.floors[f.key] =
        typeof answer === 'number' ? floorTyped(f.unit, answer) : ''
    }
  }
  return draft
}

/**
 * The draft as stored. "Only", nothing picked, is every option and not none —
 * stored as such, so an option the org adds later is included.
 */
function answersOf(topic: Topic, d: Draft): Answers {
  const out: Answers = {}
  for (const f of topic.filters) {
    if (f.type === 'oneOf') {
      if (d.scopes[f.key] !== 'only') continue
      const picked = d.picks[f.key] ?? []
      const kept = d.kept[f.key]
      out[f.key] = picked.length > 0 ? picked : (kept ?? [NOTIFICATION_ANY])
    } else {
      const floor = floorRead(f.unit, d.floors[f.key] ?? '')
      if (floor !== null && floor > 0) out[f.key] = floor
    }
  }
  return out
}

/** Floors whose typed figure cannot be stored, by key. */
function problemsOf(topic: Topic, d: Draft): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of topic.filters) {
    if (f.type !== 'atLeast') continue
    if (Number.isNaN(floorRead(f.unit, d.floors[f.key] ?? ''))) {
      out[f.key] = 'Enter zero or more, or leave it empty.'
    }
  }
  return out
}

/**
 * A topic's narrowing, opened in the account sheet in place of its tabs. The
 * sheet navigates to it rather than stacking a panel over the list it was
 * opened from, and its commit is the sheet's own foot, handed up as it changes.
 */
export function NarrowingView({
  target,
  onDone,
  onFoot,
}: {
  target: NarrowTarget
  onDone: () => void
  onFoot: (foot: FootAction) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const save = useSaveChoice(target.spaceId, target.topic.key, target.owner)
  const { topic } = target
  const [opened] = useState(() => draftOf(topic, topic.choice.filters))
  const [current, setDraft] = useState(opened)
  const problems = problemsOf(topic, current)
  const sound = Object.keys(problems).length === 0
  const dirty = hasUnsavedChanges(opened, current)
  const label = save.isPending ? 'Saving…' : 'Save changes'
  const disabled = !dirty || !sound || save.isPending

  /* The latest draft, read when the foot is pressed rather than captured
     with it, so the foot is handed up only when what it shows changes. */
  const latest = useRef({ current, save, target, onDone })
  useLayoutEffect(() => {
    latest.current = { current, save, target, onDone }
  })
  /* The view takes the keyboard as it opens, so it does not drop to the top
     of the sheet with the list it replaced. */
  const view = useRef<HTMLDivElement>(null)
  useEffect(() => view.current?.focus(), [])
  useEffect(() => {
    onFoot({
      label,
      disabled,
      onSelect: () => {
        const {
          current: draft,
          save: send,
          target: t,
          onDone: done,
        } = latest.current
        /* How it is delivered as it stands now, not as it stood when the view
           opened: a save states the whole choice, and a tick made a moment
           earlier must not be set back. */
        const scope = scopeOf(t.spaceId, t.topic.key, t.owner)
        const pending = qc
          .getMutationCache()
          .getAll()
          .filter(
            (m) =>
              m.options.scope?.id === scope && m.state.status === 'pending',
          )
          // The cache holds every mutation untyped; a mutation in this scope is
          // only ever this topic's setChoice, whose variables these are.
          .at(-1)?.state.variables as SetChoiceVariables | undefined
        const now = pending ?? cachedChoice(qc, trpc, t)
        send.mutate(
          {
            spaceId: t.spaceId,
            topic: t.topic.key,
            email: now?.email ?? t.topic.choice.email,
            rollup: now ? now.rollup : t.topic.choice.rollup,
            filters: answersOf(t.topic, draft),
          },
          { onSuccess: done },
        )
      },
    })
  }, [label, disabled, onFoot, qc, trpc])

  return (
    <div className="setform__grid" ref={view} tabIndex={-1}>
      {topic.filters.map((f) =>
        f.type === 'oneOf' ? (
          <div key={f.key} className="setform__grid">
            <Choice<Scope>
              label={`Which ${topic.label.toLowerCase()}`}
              value={current.scopes[f.key] ?? 'all'}
              onValueChange={(scope) =>
                setDraft({
                  ...current,
                  scopes: { ...current.scopes, [f.key]: scope },
                })
              }
              options={[
                { value: 'all', label: `All ${topic.label.toLowerCase()}` },
                { value: 'only', label: `${f.label} only` },
              ]}
            />
            {current.scopes[f.key] === 'only' && (
              <Field
                label={f.label}
                optional
                info="Leave it empty for every one, including any added later."
              >
                <Select
                  multiple
                  values={current.picks[f.key] ?? []}
                  onValuesChange={(values) =>
                    setDraft({
                      ...current,
                      picks: { ...current.picks, [f.key]: values },
                    })
                  }
                  options={optionsOf(f)}
                  placeholder={`All ${f.label.toLowerCase()}`}
                  summary={
                    (current.picks[f.key] ?? []).length === 0
                      ? `All ${f.label.toLowerCase()}`
                      : `${(current.picks[f.key] ?? []).length} chosen`
                  }
                />
              </Field>
            )}
          </div>
        ) : (
          <Field
            key={f.key}
            label={f.label}
            optional
            info="Leave it empty for any amount."
            problem={problems[f.key]}
          >
            <MoneyInput
              min={0}
              value={current.floors[f.key] ?? ''}
              onChange={(e) =>
                setDraft({
                  ...current,
                  floors: { ...current.floors, [f.key]: e.target.value },
                })
              }
            />
          </Field>
        ),
      )}
      {save.error && <Notice tone="error">{save.error.message}</Notice>}
    </div>
  )
}
